import { getCoursesBatchesList, getSessionList } from 'action/attendanceModule';
import CustomPagination from 'components/CommonComponents/ReactTable/CustomPagination';
import AdminHeader from 'components/Headers/AdminHeader';
import { coursesBatchesList, sessionsList } from 'constants/attendanceModule';
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardBody, CardHeader, CardTitle, Container, Label, Popover, PopoverBody, UncontrolledTooltip } from 'reactstrap';
import Select, { components } from "react-select";
import ManageOfflineAttendance from './ManageOfflineAttendance';
import { BATCHS_STATUS_OPTIONS, DEFAULT_RECORDS_PER_PAGE } from 'components/constant';
import { showAlert } from 'components/CommonComponents/CommonFunctions';
import DatePicker from 'react-datepicker';
import { fixedWidthCustomStyle } from 'constants/CommonConstants';
import { format, isAfter, startOfDay } from 'date-fns';

export default function OfflineAttendance() {
    const dispatch = useDispatch();
    const sessionListRef = useRef(null);
    const [alternative, setAlternative] = useState({
        isLoading: true,
        error: "",
        noRecords: false,
        showLoaderOnClick: false,
    });
    const [sessionAlternative, setSessionAlternative] = useState({
        isLoading: true,
        error: "",
        noRecords: false,
        noHeaderRecords: false,
        showLoaderOnClick: false,
    });
    const [page, setPage] = useState({ coursePage: 1, sessionPage: 1 });
    const [activeValues, setActiveValues] = useState({ courseId: null, batchId: null, reloaded: false });
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [showContent, setShowContent] = useState(window.innerWidth >= 1200);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        status: null,
        batchDate: [null, null],
    });
    const [detailsRightPanel, setDetailsRightPanel] = useState({
        isOpen: false,
        mode: "Add", // "Add" or "Edit" or "View"
        batch: { id: null, name: null },
        course: { id: null, name: null },
        session: { id: null, name: null },
        batchDates: { batchStartTime: null, batchEndTime: null },
    });
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [debouncedLesson, setDebouncedLesson] = useState("");

    const coursesBatchesData = useSelector((state) => state.attendanceModule.coursesBatchesList);
    const sessionData = useSelector((state) => state.attendanceModule.sessionsList);

    const activeFilterCount = Object.values(filters).filter((value) => {
        if (Array.isArray(value)) {
            return value.some(Boolean);
        }

        return value !== null && value !== "";
    }).length;

    useEffect(() => {
        setAlternative((prev) => ({
            ...prev,
            isLoading: true,
        }));
        getCoursesBatchesList(dispatch, page.coursePage, appliedFilters, debouncedSearch);
        return () => {
            dispatch({ type: coursesBatchesList, payload: {} });
            dispatch({ type: sessionsList, payload: {} });
        };
    }, [debouncedSearch, appliedFilters, page.coursePage]);

    useEffect(() => {
        if (coursesBatchesData?.code === 200) {
            setAlternative({
                isLoading: false,
                error: "",
                noRecords: coursesBatchesData?.data?.courses?.length === 0,
                showLoaderOnClick: false,
            });

            // // No courses -> sessions request will never happen
            if (coursesBatchesData?.data?.courses?.length === 0) {
                setSessionAlternative({
                    isLoading: false,
                    error: "",
                    noRecords: true,
                    noHeaderRecords: true,
                    showLoaderOnClick: false,
                });
            }

        } else if (coursesBatchesData?.code || coursesBatchesData?.message) {
            setAlternative({
                isLoading: false,
                error:
                    coursesBatchesData?.message ||
                    "Failed to load courses and batches! Please try again.",
                noRecords: false,
                showLoaderOnClick: false,
            });

            // // Stop sessions loader because lesson API won't be called
            setSessionAlternative({
                isLoading: false,
                error: "Failed to load sessions! Please try again.",
                noRecords: false,
                noHeaderRecords: false,
                showLoaderOnClick: false,
            });
        }
    }, [coursesBatchesData]);

    useEffect(() => {
        if (activeValues.courseId && activeValues.batchId && activeValues.reloaded) {
            setPage(prev => ({
                ...prev,
                sessionPage: 1,
            }));
            setSessionAlternative(prev => ({
                ...prev,
                isLoading: true,
            }));
            setActiveValues((prev) => ({ ...prev, reloaded: false }));

            getSessionList(dispatch, activeValues.courseId, activeValues.batchId, page.sessionPage);
        } else {
            const courses = coursesBatchesData?.data?.courses || [];
            if (courses.length > 0 && courses[0].batches?.length > 0) {
                const firstCourse = courses[0];
                const firstBatch = firstCourse.batches[0];

                setActiveValues({ courseId: firstCourse.courseId, batchId: firstBatch.batchId, reloaded: false });
                setPage((prev) => ({
                    ...prev,
                    sessionPage: 1,
                }));
                setSessionAlternative((prev) => ({
                    ...prev,
                    isLoading: true,
                }));

                getSessionList(dispatch, firstCourse.courseId, firstBatch.batchId, page.sessionPage);
            }
        }

    }, [coursesBatchesData]);

    useEffect(() => {
        if (!activeValues.courseId || !activeValues.batchId) return;

        setSessionAlternative((prev) => ({
            ...prev,
            isLoading: true,
        }));

        getSessionList(dispatch, activeValues.courseId, activeValues.batchId, page.sessionPage);
    }, [debouncedLesson, page.sessionPage,]);


    useEffect(() => {
        if (sessionData?.code === 200) {
            setSessionAlternative({
                isLoading: false,
                error: "",
                noRecords: sessionData?.data?.sessions?.length === 0,
                noHeaderRecords: sessionData?.data ? false : true,
                showLoaderOnClick: false,
            });

        } else if (sessionData?.code || sessionData?.message) {
            setSessionAlternative({
                isLoading: false,
                error:
                    sessionData?.message ||
                    "Failed to load lessons! Please try again.",
                noRecords: false,
                noHeaderRecords: false,
                showLoaderOnClick: false,
            });
        }
    }, [sessionData]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        sessionListRef.current?.scrollIntoView({
            behavior: "smooth",
            top: 0
        });
    }, [page.sessionPage, activeValues.batchId]);

    // useEffect(() => {
    //     const timer = setTimeout(() => {
    //         setDebouncedLesson(lessonSearch.trim());
    //     }, 500);

    //     return () => clearTimeout(timer);
    // }, [lessonSearch]);

    // useEffect(() => {
    //     setPageNum(1);
    //     getCoursesBatchesList(dispatch, pageNum, debouncedSearch);
    // }, [debouncedSearch]);


    const handleBatchSelect = (courseId, batchId) => {
        setSessionAlternative((prev) => ({
            ...prev,
            isLoading: true,
        }));

        setActiveValues({ courseId, batchId });

        setPage(prev => ({
            ...prev,
            sessionPage: 1,
        }));

        getSessionList(dispatch, courseId, batchId, page.sessionPage);

        if (window.innerWidth < 1200) {
            setShowContent(true);
        }

        // const filter = debouncedLesson.trim()
        //     ? [
        //         {
        //             field: "LessonName",
        //             condition: "like",
        //             filterValues: [debouncedLesson?.trim()],
        //         },
        //     ]
        //     : [];

        // getLessons(
        //     dispatch,
        //     courseId,
        //     batchId,
        //     page.sessionPage,
        //     filter
        // );


    };

    // const getBatchInitials = (text = "") => {
    //     const words = text.trim().split(/\s+/).filter(Boolean);

    //     if (words.length === 0) return "";

    //     // Single word -> first 2 letters
    //     if (words.length === 1) {
    //         return words[0].substring(0, 2).toUpperCase();
    //     }

    //     // Multiple words -> first letter of first 2 words
    //     return (words[0][0] + words[1][0]).toUpperCase();
    // };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            setDebouncedSearch(search.trim());
        }
    };

    const handleClear = () => {
        setAlternative((prev) => ({
            ...prev,
            isLoading: true,
        }));
        setSearch("");
        setDebouncedSearch("");
        setPage((prev) => ({ ...prev, coursePage: 1 }));
    };

    const handleClearFilters = () => {
        if (activeFilterCount > 0) {
            setFilters({
                status: null,
                batchDate: [null, null],
            });

            setAppliedFilters([]);

            setPage((prev) => ({
                ...prev,
                coursePage: 1,
            }));

            setShowFilters(false);
        } else {
            setShowFilters(false);
        }
    };

    const handleApplyFilters = () => {
        if (activeFilterCount > 0) {
            const newFilters = buildFilterParams();
            setAppliedFilters(newFilters);
            setPage((prev) => ({
                ...prev,
                coursePage: 1,
            }));

            setShowFilters(false);
        }
        else {
            setShowFilters(false);
        }
    };

    const totalPages = Math.ceil(
        coursesBatchesData?.data?.totalFilteredRecords / 5,
    )

    const totalSessionPages = Math.ceil(
        sessionData?.data?.totalRecords / sessionData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE,
    )

    const handleCloseAttendanceModel = () => {
        setDetailsRightPanel((prev) => ({
            ...prev,
            isOpen: false,
        }));
    };

    const handleAddAttendance = (batchData) => {
        if (batchData?.totalTrainees === 0) {
            showAlert({
                title: "No Enrolled Trainees",
                type: "warning",
                message:
                    "No trainees are enrolled in this batch. Attendance cannot be marked.",
                showCancel: false,
                confirmText: "OK",
                confirmStyle: "default",
            });
            return;
        }

        const sessionStartDate = new Date(batchData?.batchStartDate);
        const today = startOfDay(new Date());

        // Prevent attendance for a future session
        if (isAfter(startOfDay(sessionStartDate), today)) {
            showAlert({
                title: "Batch Not Started",
                type: "warning",
                message:
                    "Attendance can’t be marked because this batch hasn’t started yet.",
                showCancel: false,
                confirmText: "OK",
                confirmStyle: "default",
            });
            return;
        }

        setDetailsRightPanel({
            isOpen: true,
            mode: "Add",
            batch: { id: batchData.batchId, name: batchData?.batchName, contentDeliveryMode: batchData.contentDeliveryMode },
            course: { id: batchData.courseId, name: batchData?.courseName },
            session: { id: null, name: null },
            batchDates: { batchStartTime: batchData.batchStartTime, batchEndTime: batchData.batchEndTime },
        });
    };

    const handleSaveAttendance = (courseId, batchId, reloaded = true) => {
        setActiveValues({ courseId, batchId, reloaded });
        getSessionList(dispatch, courseId, batchId, page.sessionPage);
        getCoursesBatchesList(dispatch, page.coursePage, appliedFilters, debouncedSearch);
    }

    const handleEditAttendance = (batchData, session) => {
        setDetailsRightPanel({
            isOpen: true,
            mode: "Edit",
            batch: { id: batchData.batchId, name: batchData?.batchName, contentDeliveryMode: batchData.contentDeliveryMode },
            course: { id: batchData.courseId, name: batchData?.courseName },
            session: { id: session.sessionId, name: null },
        });
    }

    const handleViewAttendance = (batchData, session) => {
        setDetailsRightPanel({
            isOpen: true,
            mode: "View",
            batch: { id: batchData.batchId, name: batchData?.batchName, contentDeliveryMode: batchData.contentDeliveryMode },
            course: { id: batchData.courseId, name: batchData?.courseName },
            session: { id: session.sessionId, name: null },
        });
    }

    const buildFilterParams = () => {
        const filterParams = [];

        if (filters.status?.value) {
            filterParams.push({
                field: "Status",
                condition: "eq",
                filterValues: [filters.status.value],
            });
        }

        const [fromDate, toDate] = filters.batchDate || [];

        if (fromDate && toDate) {
            filterParams.push({
                field: "BatchStartDate",
                condition: "btw",
                filterValues: [
                    format(fromDate, "yyyy-MM-dd"),
                    format(toDate, "yyyy-MM-dd"),
                ],
            });
        } else if (fromDate) {
            filterParams.push({
                field: "BatchStartDate",
                condition: "eq",
                filterValues: [format(fromDate, "yyyy-MM-dd"),],
            });
        } else if (toDate) {
            filterParams.push({
                field: "BatchEndDate",
                condition: "lt",
                filterValues: [format(toDate, "yyyy-MM-dd")],
            });
        }

        return filterParams.length ? JSON.stringify(filterParams) : [];
    };

    // Custom Option Renderer
    const Option = (props) => {
        return (
            <components.Option {...props}>
                <span className={`batch-dropdown-status ${props.data.value}`}>
                    {props.data.label}
                </span>
            </components.Option>
        );
    };

    // Custom SingleValue Renderer (for the selected value)
    const SingleValue = (props) => {
        return (
            <components.SingleValue {...props}>
                <span className={`batch-dropdown-status ${props.data.value}`}>
                    {props.data.label}
                </span>
            </components.SingleValue>
        );
    };

    // const handleLessonSearchKeyDown = (e) => {
    //     if (e.key === "Enter") {
    //         e.preventDefault();
    //         setDebouncedLesson(lessonSearch.trim());
    //     }
    // };

    // const handleLessonSearchClear = () => {
    //     setLessonSearch("");
    //     setDebouncedLesson("");
    //     setPage((prev) => ({ ...prev, lessonPage: 1 }));
    // }

    return (
        <>
            <AdminHeader />
            <Container fluid className="mt--6">
                <div className="attendance-page">
                    <Card className={`sidebar ${showContent ? "sidebar-hide-mobile" : ""
                        }`}>
                        <CardHeader className="section-header">
                            <CardTitle tag="h3" className="mb-0 text-default">
                                Courses and Batches
                            </CardTitle>
                        </CardHeader>
                        <CardBody className="sidebar-scroll">
                            <>
                                <div className="search-filter-bar">

                                    <div className="search-box">
                                        <i className="fa fa-search" />

                                        <input
                                            type="text"
                                            placeholder="Search course or batch"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            onKeyDown={handleSearchKeyDown}
                                        />

                                        {search && (
                                            <button
                                                className="search-clear-btn"
                                                onClick={handleClear}
                                            >
                                                <i className="fa fa-times" />
                                            </button>
                                        )}
                                    </div>

                                    <Button
                                        id="filterButton"
                                        className="attendance-filter-btn"
                                        color="light"
                                    >
                                        <i className="fa fa-sliders" />
                                        Filters

                                        {activeFilterCount > 0 && (
                                            <span className="filter-count">
                                                {activeFilterCount}
                                            </span>
                                        )}
                                    </Button>

                                </div>

                                <Popover
                                    placement="bottom-end"
                                    target="filterButton"
                                    trigger="legacy"
                                    isOpen={showFilters}
                                    toggle={() => setShowFilters(!showFilters)}
                                    className="filter-popover"
                                >
                                    <PopoverBody>

                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            <h5 className="filter-title">
                                                Filters
                                            </h5>

                                            <i className="fa-solid fa-xmark cursor text-muted"
                                                onClick={() => setShowFilters(!showFilters)}
                                            />
                                        </div>

                                        <div className="attendace-filter-group">

                                            <Label>Status</Label>
                                            <Select
                                                className="mr-2 mb-2 filter-select"
                                                options={BATCHS_STATUS_OPTIONS}
                                                placeholder="Select status"
                                                value={filters.status}
                                                onChange={(selected) =>
                                                    setFilters((prev) => ({
                                                        ...prev,
                                                        status: selected,
                                                    }))
                                                }
                                                classNamePrefix="react-select"
                                                styles={fixedWidthCustomStyle("100%")}
                                                components={{ Option, SingleValue }}
                                            />

                                        </div>

                                        <div className="attendace-filter-group d-flex flex-column">

                                            <Label>Batch Start Date</Label>

                                            <div className="date-row">

                                                <DatePicker
                                                    selectsRange
                                                    startDate={filters.batchDate[0]}
                                                    endDate={filters.batchDate[1]}
                                                    selected={filters.batchDate[0]}
                                                    onChange={(dates) =>
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            batchDate: dates,
                                                        }))
                                                    }
                                                    isClearable={!!filters.batchDate[0]}
                                                    placeholderText="Select a date or date range"
                                                    className="form-control filter-input-large"
                                                    dateFormat="dd-MMM-yyyy"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={100}
                                                    calendarClassName="custom-calendar"
                                                    onPaste={(e) => e.preventDefault()}
                                                    onKeyDown={(e) => {
                                                        if (e.key !== "Enter") {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />

                                            </div>

                                        </div>

                                        <div className="filter-footer">

                                            <Button
                                                color="secondary"
                                                className="clear-filter-btn"
                                                onClick={handleClearFilters}
                                            >
                                                Clear
                                            </Button>

                                            <Button
                                                color="deep-blue"
                                                onClick={handleApplyFilters}
                                            >
                                                Apply
                                            </Button>

                                        </div>

                                    </PopoverBody>
                                </Popover>
                                <span className="total-records mb-0">
                                    {activeFilterCount > 0 ? (
                                        <span >
                                            Showing{" "} {coursesBatchesData?.data?.totalFilteredRecords}{" "}
                                            of {coursesBatchesData?.data?.totalRecords}{" "}
                                            Records
                                        </span>
                                    ) :
                                        (<span > Total Records : {coursesBatchesData?.data?.totalRecords}</span>)}
                                </span>

                                {alternative.isLoading ? (
                                    <div className="loaderContainer">
                                        <i className="fa fa-spinner fa-spin fa-2xl loader" />
                                    </div>
                                ) : alternative.error ? (
                                    <>
                                        <div className="loaderContainer error-style">
                                            <div className="error-icon">
                                                <i class="fa-solid fa-circle-exclamation" />
                                            </div>
                                            <span>{alternative.error}</span>
                                        </div>
                                    </>
                                ) : alternative.noRecords ?
                                    <div className="loaderContainer empty-style">
                                        <div className="empty-icon">
                                            <i class="fa-solid fa-circle-info" />
                                        </div>
                                        <span>No courses found.</span>
                                        {
                                            page.coursePage > 1 && coursesBatchesData?.data?.courses?.length === 0 &&
                                            <Button
                                                className="show-all-btn"
                                                onClick={handleClear}
                                            >
                                                Show All Courses
                                            </Button>
                                        }
                                    </div>
                                    : (
                                        <>
                                            {coursesBatchesData?.data?.courses?.map((course, index) => (
                                                <div
                                                    key={course?.courseId}
                                                    className={`course-section ${index !== coursesBatchesData?.data?.courses?.length - 1 ? "course-divider" : ""
                                                        }`}
                                                >
                                                    <div className="course-heading">
                                                        <h3 className="text-deep-blue">
                                                            <span className="text-muted mr-1">Course Title:</span>
                                                            {course?.courseName}
                                                        </h3>
                                                        <label>{course?.courseLevel}</label>
                                                    </div>

                                                    {course?.batches?.length > 0 ? (
                                                        course?.batches?.map((batch, index) => (
                                                            <div
                                                                key={batch.batchId || index}
                                                                className={`batch-card ${activeValues.batchId === batch.batchId ? "active" : ""
                                                                    }`}
                                                                onClick={() => handleBatchSelect(course.courseId, batch.batchId)}
                                                            >
                                                                <span className={`batch-status ${batch.batchStatus}`}>
                                                                    {BATCHS_STATUS_OPTIONS.find(
                                                                        (status) => status.value === batch.batchStatus
                                                                    )?.label || "-"}
                                                                </span>
                                                                {/* <div className="batch-avatar"> {getBatchInitials(batch.batchName)}</div> */}

                                                                <div className="batch-info">
                                                                    <h3>
                                                                        <span className='value' id={`bname${batch?.batchId}`}>{batch?.batchName || "-"}</span>
                                                                        <UncontrolledTooltip delay={0} target={`bname${batch?.batchId}`} innerClassName="custom-tooltip">
                                                                            {batch?.batchName}
                                                                        </UncontrolledTooltip>
                                                                    </h3>
                                                                    <h4 className='d-flex align-items-center'>
                                                                        <i className='fa fa-calendar mt-1' />
                                                                        {batch?.batchStartDate} – {batch?.batchEndDate}
                                                                    </h4>
                                                                    <h4 className='d-flex align-items-center'>
                                                                        <i className='fa fa-clock mt-1' />
                                                                        Daily • {batch?.batchStartTime} – {batch?.batchEndTime}
                                                                    </h4>
                                                                    <h4 className='d-flex align-items-center'>
                                                                        <i className="fa-solid fa-map-pin mt-1" />
                                                                        <span className='sub-value' id={`loc${batch?.batchId}`}>{batch.trainingCentre || "-"}</span>
                                                                        <UncontrolledTooltip delay={0} target={`loc${batch?.batchId}`} innerClassName="custom-tooltip">
                                                                            {batch?.trainingCentre}
                                                                        </UncontrolledTooltip>
                                                                    </h4>
                                                                    <h4 className='d-flex align-items-center'>
                                                                        <i className="fa-solid fa-users mr-2" />
                                                                        {batch?.totalTrainees} trainees
                                                                    </h4>
                                                                    {/* <h4 className='d-flex align-items-center'>
                                                                        <i className="fa-solid fa-hourglass-half" />
                                                                        {batch?.completedLessons} / {batch?.totalLessons} lessons
                                                                    </h4> */}
                                                                </div>

                                                                <i className="fa fa-chevron-right action" id={`details${batch?.batchId}`} />
                                                                <UncontrolledTooltip delay={0} target={`details${batch?.batchId}`}>
                                                                    View Batch Details
                                                                </UncontrolledTooltip>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="loaderContainer empty-style">
                                                            <div className="empty-icon">
                                                                <i class="fa-solid fa-circle-info" />
                                                            </div>
                                                            <span> No batches available for this course.</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {totalPages > 1 && (
                                                <CustomPagination
                                                    totalPages={totalPages}
                                                    activePage={page.coursePage}
                                                    setPageNum={(page) => setPage((prev) => ({ ...prev, coursePage: page }))}
                                                />
                                            )}
                                        </>
                                    )}
                            </>
                        </CardBody>
                    </Card>


                    <div className={`dashboard-content ${!showContent ? "content-hide-mobile" : ""
                        }`} ref={sessionListRef}>
                        <Button
                            className="mobile-back-btn"
                            onClick={() => setShowContent(false)}
                        >
                            <i className="fa fa-arrow-left mr-2" />
                            Courses & Batches
                        </Button>

                        {sessionAlternative.isLoading ?
                            <Card className="right-content">
                                <CardBody className="right-side-error">
                                    <div className="loaderContainer">
                                        <i className="fa fa-spinner fa-spin fa-2xl loader" />
                                    </div>
                                </CardBody>
                            </Card>
                            : sessionAlternative.error ? (
                                <Card className="right-content">
                                    <CardBody className="right-side-error">
                                        <div className="loaderContainer error-style">
                                            <div className="error-icon">
                                                <i class="fa-solid fa-circle-exclamation" />
                                            </div>
                                            <span>{alternative.error}</span>
                                        </div>
                                    </CardBody>
                                </Card>
                            ) : sessionAlternative.noHeaderRecords && sessionAlternative.noRecords ?
                                <Card className="right-content">
                                    <CardBody className="right-side-error">
                                        <div className="loaderContainer empty-style">
                                            <div className="empty-icon">
                                                <i class="fa-solid fa-circle-info" />
                                            </div>
                                            <span>No attendance sessions found.</span>
                                        </div>
                                    </CardBody>
                                </Card>
                                :
                                <>
                                    {!sessionAlternative.noHeaderRecords && sessionData?.data &&
                                        <Card className="right-header">
                                            <div className="top-header">
                                                <div>
                                                    <h2>{sessionData?.data?.courseName}</h2>

                                                    <h1>{sessionData?.data?.batchName}</h1>

                                                    <div className="course-meta">
                                                        <span>
                                                            <i className='fa fa-users' />
                                                            {sessionData?.data?.totalTrainees || 0} trainees
                                                        </span>

                                                        {/* <span>
                                                        <i className='fa fa-calendar' />
                                                        Mon–Fri
                                                    </span> */}

                                                        <span className="d-flex align-items-center">
                                                            <span className="language-badge">
                                                                {sessionData?.data?.batchLanguage === "ENGLISH"
                                                                    ? "E"
                                                                    : sessionData?.data?.batchLanguage === "HINDI"
                                                                        ? "H"
                                                                        : "EH"}
                                                            </span>
                                                            {sessionData?.data?.batchLanguage}
                                                        </span>

                                                        <span>
                                                            <i className='fa fa-clock' />
                                                            {sessionData?.data?.batchStartTime} - {sessionData?.data?.batchEndTime}
                                                        </span>

                                                        <span>

                                                            <i className='fa fa-calendar' />
                                                            {sessionData?.data?.batchStartDate} – {sessionData?.data?.batchEndDate}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    }

                                    {(sessionData?.data?.batchStatus === "CANCELLED" || sessionData?.data?.batchStatus === "INACTIVE") && (
                                        <Card className="batch-status-alert">
                                            <div className="batch-status-alert-content">
                                                <div className="batch-status-alert-icon">
                                                    <i className="fa-solid fa-circle-exclamation"></i>
                                                </div>

                                                <div className="batch-status-alert-text">
                                                    {sessionData?.data?.batchStatus === "CANCELLED"
                                                        ? "Note: This batch has been cancelled. Attendance cannot be added or updated for a cancelled batch."
                                                        : "Note: This batch is currently inactive. Attendance cannot be added or updated for an inactive batch."}
                                                </div>
                                            </div>
                                        </Card>
                                    )}

                                    <div className="syllabus">
                                        {sessionAlternative.noRecords ?
                                            <Card className="session-content  ml-0">
                                                <CardHeader className="d-flex align-items-center justify-content-end">
                                                    {(sessionData?.data?.batchStatus !== "COMPLETED" && sessionData?.data?.batchStatus !== "CANCELLED" && sessionData?.data?.batchStatus !== "INACTIVE") && (
                                                        <Button
                                                            color="deep-blue"
                                                            onClick={() =>
                                                                handleAddAttendance(sessionData?.data)
                                                            }
                                                        >
                                                            <i className="fa fa-plus" /> Add Attendance
                                                        </Button>
                                                    )}
                                                </CardHeader>
                                                <CardBody className="sessionContainer">

                                                    <div className=" empty-style">
                                                        <div className="empty-icon">
                                                            <i class="fa-solid fa-circle-info" />
                                                        </div>
                                                        <span>No attendance sessions found.</span>
                                                    </div>
                                                </CardBody>
                                            </Card>
                                            : (
                                                <>
                                                    <div className="session-list">
                                                        {(sessionData?.data?.batchStatus !== "COMPLETED" && sessionData?.data?.batchStatus !== "CANCELLED" && sessionData?.data?.batchStatus !== "INACTIVE") && (
                                                            <div className="d-flex align-items-center justify-content-end pb-4">
                                                                <Button
                                                                    color="deep-blue"
                                                                    onClick={() =>
                                                                        handleAddAttendance(sessionData?.data)
                                                                    }
                                                                >
                                                                    <i className="fa fa-plus" /> Add Attendance
                                                                </Button>
                                                            </div>
                                                        )}
                                                        {sessionData?.data?.sessions?.length > 0 && sessionData?.data?.sessions?.map((session, index) => {
                                                            const sessionNumber =
                                                                (page.sessionPage - 1) * DEFAULT_RECORDS_PER_PAGE + index + 1;
                                                            return (
                                                                <div className="lesson-card">
                                                                    <div className="session-card">
                                                                        {/* <span className={`session-status ${session.sessionStatus}`}>
                                                                                    {session.sessionStatus === "COMPLETED"
                                                                                        ? "Completed"
                                                                                        : session.sessionStatus === "ONGOING"
                                                                                            ? "Ongoing"
                                                                                            : "Not Started"}
                                                                                </span> */}
                                                                        <div className="session-left">
                                                                            <div className="session-circle">
                                                                                {sessionNumber}
                                                                            </div>
                                                                            <div>
                                                                                <h5 className="session-title">Session {sessionNumber}</h5>
                                                                                <div className="session-meta">
                                                                                    {/* <h5 className="dot"></h5> */}
                                                                                    <h5 className="mr-2">
                                                                                        <i className="fa fa-calendar mr-1" />
                                                                                        {/* Session Date:{" "} */}
                                                                                        {session?.sessionDate}
                                                                                    </h5>
                                                                                    {/* <h5 className="dot ml-2"></h5> */}
                                                                                    <h5>
                                                                                        <i className="fa fa-clock mr-1" />
                                                                                        {/* Timing:{" "} */}
                                                                                        {session?.sessionStartTime}{" "}
                                                                                        - {session?.sessionEndTime}
                                                                                    </h5>
                                                                                </div>
                                                                                <div className="session-meta mt-0">
                                                                                    {/* <h5 className="dot"></h5> */}
                                                                                    <h5 className="mr-2">
                                                                                        <i className="fa fa-check-circle mr-1" />
                                                                                        {session?.presentCount} Present
                                                                                    </h5>
                                                                                    {/* <h5 className="dot  ml-2"></h5> */}
                                                                                    <h5>
                                                                                        <i className="fa fa-times-circle mr-1" />
                                                                                        {session?.absentCount} Absent
                                                                                    </h5>
                                                                                </div>
                                                                                <div className="session-meta mt-0">
                                                                                    {session?.isLocked ? (
                                                                                        <h5 className="session-edit-info">
                                                                                            {/* <i className="fa fa-lock mr-1" /> */}
                                                                                            Note: Editing is no longer available for this session.
                                                                                        </h5>
                                                                                    ) : (
                                                                                        <h5 className="session-edit-info">
                                                                                            {/* <i className="fa fa-clock mr-1" /> */}
                                                                                            Note: Editing available until{" "}
                                                                                            {session?.attendanceLockedAt}. After that, editing is unavailable.
                                                                                        </h5>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div className="toolbar">
                                                                            <Button id={`view${session?.sessionId}`}
                                                                                onClick={() => handleViewAttendance(sessionData?.data, session)}
                                                                            >
                                                                                <i className="fa fa-eye" />
                                                                            </Button>
                                                                            <UncontrolledTooltip delay={0} target={`view${session?.sessionId}`}>
                                                                                View Attendance
                                                                            </UncontrolledTooltip>

                                                                            {(!session?.isLocked && sessionData?.data?.batchStatus !== "CANCELLED" && sessionData?.data?.batchStatus !== "INACTIVE") && <>
                                                                                <Button id={`edit${session?.sessionId}`}
                                                                                    onClick={() => handleEditAttendance(sessionData?.data, session)}
                                                                                >
                                                                                    <i className="fa fa-pen" />
                                                                                </Button>
                                                                                <UncontrolledTooltip delay={0} target={`edit${session?.sessionId}`}>
                                                                                    Edit Attendance
                                                                                </UncontrolledTooltip>
                                                                            </>}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>

                                                    {totalSessionPages > 1 && (
                                                        <CustomPagination
                                                            totalPages={totalSessionPages}
                                                            activePage={page.sessionPage}
                                                            setPageNum={(page) => setPage((prev) => ({ ...prev, sessionPage: page }))}
                                                        />
                                                    )}
                                                </>
                                            )
                                        }
                                    </div>
                                </>}
                    </div>
                </div>
            </Container>
            <ManageOfflineAttendance
                openAttendaceModel={detailsRightPanel.isOpen}
                mode={detailsRightPanel.mode}
                handleCloseModel={handleCloseAttendanceModel}
                batch={detailsRightPanel.batch}
                course={detailsRightPanel.course}
                session={detailsRightPanel.session}
                onSaveAttendace={handleSaveAttendance}
                batchDates={detailsRightPanel.batchDates}
            />
        </>
    );
}